'use client';

import type { CSSProperties, ReactNode } from 'react';
import styles from './Carousel.module.css';
import CarouselClient from './CarouselClient';

export type CarouselItem = {
  id: string;
  content: ReactNode;
};

export type ResponsivePerView = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

export type ResponsivePeek = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

export type ResponsivePadStart = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

export type ResponsivePadEnd = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

type Props = {
  items: CarouselItem[];
  carouselId: string;
  perView?: number | ResponsivePerView;
  peek?: number | ResponsivePeek;
  padStart?: number | ResponsivePadStart;
  padEnd?: number | ResponsivePadEnd;
  gap?: number;
  className?: string;
  ariaLabel?: string;
  children?: ReactNode;
};

/**
 * Client-safe version of `Carousel` (which is a Server Component).
 * Same markup + CSS + `CarouselClient` behavior, but can be imported from Client Components.
 */
export function CarouselClientOnly({
  items,
  carouselId,
  perView = 1,
  peek = 0,
  padStart = 0,
  padEnd = 0,
  gap = 16,
  className,
  ariaLabel = 'Carousel',
  children,
}: Props) {
  const getResponsiveStyles = () => {
    const perViewStyles = (() => {
      if (typeof perView === 'number') {
        return {
          '--per-view-mobile': String(perView),
          '--per-view-tablet': String(perView),
          '--per-view-desktop': String(perView),
        };
      }
      const defaults = { mobile: 1, tablet: 2, desktop: 3 };
      const responsive = { ...defaults, ...perView };
      return {
        '--per-view-mobile': String(responsive.mobile),
        '--per-view-tablet': String(responsive.tablet),
        '--per-view-desktop': String(responsive.desktop),
      };
    })();

    const peekStyles = (() => {
      if (typeof peek === 'number') {
        return {
          '--peek-mobile': `${peek}px`,
          '--peek-tablet': `${peek}px`,
          '--peek-desktop': `${peek}px`,
        };
      }
      const defaults = { mobile: 0, tablet: 0, desktop: 0 };
      const responsive = { ...defaults, ...peek };
      return {
        '--peek-mobile': `${responsive.mobile}px`,
        '--peek-tablet': `${responsive.tablet}px`,
        '--peek-desktop': `${responsive.desktop}px`,
      };
    })();

    const padStartStyles = (() => {
      if (typeof padStart === 'number') {
        return {
          '--pad-start-mobile': `${padStart}px`,
          '--pad-start-tablet': `${padStart}px`,
          '--pad-start-desktop': `${padStart}px`,
        };
      }
      const defaults = { mobile: 0, tablet: 0, desktop: 0 };
      const responsive = { ...defaults, ...padStart };
      return {
        '--pad-start-mobile': `${responsive.mobile}px`,
        '--pad-start-tablet': `${responsive.tablet}px`,
        '--pad-start-desktop': `${responsive.desktop}px`,
      };
    })();

    const padEndStyles = (() => {
      if (typeof padEnd === 'number') {
        return {
          '--pad-end-mobile': `${padEnd}px`,
          '--pad-end-tablet': `${padEnd}px`,
          '--pad-end-desktop': `${padEnd}px`,
        };
      }
      const defaults = { mobile: 0, tablet: 0, desktop: 0 };
      const responsive = { ...defaults, ...padEnd };
      return {
        '--pad-end-mobile': `${responsive.mobile}px`,
        '--pad-end-tablet': `${responsive.tablet}px`,
        '--pad-end-desktop': `${responsive.desktop}px`,
      };
    })();

    return { ...perViewStyles, ...peekStyles, ...padStartStyles, ...padEndStyles };
  };

  const styleVars = {
    ...getResponsiveStyles(),
    '--gap': `${gap}px`,
  } as CSSProperties;

  return (
    <section
      className={`${styles.root} ${className ?? ''}`}
      aria-label={ariaLabel}
      style={styleVars}
      data-carousel-id={carouselId}
      id={carouselId}
    >
      <ul className={`${styles.track} ${carouselId}-track`} role="list">
        {items.map(item => (
          <li key={item.id} id={item.id} className={`${styles.slide} ${carouselId}-slide`}>
            {item.content}
          </li>
        ))}
      </ul>
      {children}
      <CarouselClient carouselId={carouselId} />
    </section>
  );
}
